import { EDR_INBOX_ROLES, ROLES, isAmMroDomainOverride } from '../../../constants/roles';
import { canApprove } from '../../../utils/roleHierarchy';
import { canUserChangeWorkOrderScheduledDate } from '../../../utils/workOrderScheduledDatePolicy';
import { roleMatches } from '../../../utils/rolePermissions';
import { needsStoreIntentAction } from '../../../utils/workOrderStoreReview';
import { getAmMroMaintenanceStepWarning } from '../../../utils/amMroConsecutiveApproval';

/**
 * Custom hook for maintenance permission checks
 */
export const useMaintenancePermissions = (user) => {
  // Full org AM / Super Admin — CAMO QM and org-wide overrides
  const isAmOverride = [ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
  // AM MRO shares AM authority in MRO, Planning, Store, and Procurement only (no CAMO TO)
  const isDomainOverride = isAmMroDomainOverride(user?.role);
  // Default override used for planning / store / MRO workflow actions
  const isOverride = isDomainOverride;

  const amMroBlocked = (wo, step) => Boolean(getAmMroMaintenanceStepWarning(user, wo, step));
  const amMroWarning = (wo, step) => getAmMroMaintenanceStepWarning(user, wo, step);

  const STANDALONE_EMERGENCY_ROLES = [
    ROLES.SUPER_ADMIN,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.AM_MRO,
    ROLES.CA_MANAGER,
    ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER
  ];

  const isStandaloneEmergencyActor = (wo) =>
    Boolean(wo?.standaloneEmergencyRepair) && STANDALONE_EMERGENCY_ROLES.includes(user?.role);

  const canStandAloneEmergencyWorkOrder = STANDALONE_EMERGENCY_ROLES.includes(user?.role);

  const CAMO_QM_ACK_ROLES = [ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER];
  const MRO_QM_ACK_ROLES = [ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER];

  const canViewStandaloneEmergencyTab =
    canStandAloneEmergencyWorkOrder || CAMO_QM_ACK_ROLES.includes(user?.role);

  const isAwaitingCamoReworkAfterQmReject = (wo) =>
    Boolean(wo?.camoQmRejectedAt && !wo?.camoQmAcknowledged);

  /** Cancelled WOs are terminal — no role may mutate them (view only). */
  const isCancelledWorkOrder = (wo) => wo?.status === 'CANCELLED';

  const canAcknowledge = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    if (isAwaitingCamoReworkAfterQmReject(wo)) return false;
    // CAMO QM ack — org AM / Super Admin only (AM_MRO has no CAMO TO / QM authority)
    const hasRole = CAMO_QM_ACK_ROLES.includes(user?.role) || isAmOverride;
    return hasRole &&
           (isAmOverride || wo.status === 'SCHEDULED') &&
           !wo.camoQmAcknowledged;
  };

  const canPlan = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    if (isAwaitingCamoReworkAfterQmReject(wo)) return false;
    if (amMroBlocked(wo, 'planning')) return false;
    const hasRole = user?.role === ROLES.PLANNING_MANAGER || isOverride;
    return hasRole &&
           (isOverride || wo.camoQmAcknowledged) &&
           !wo.planningApproved;
  };

  const canProvideProcurementRequirements = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    const hasRole = user?.role === ROLES.PLANNING_MANAGER || isOverride;
    return hasRole && 
           wo.needsStoreAction && 
           !wo.isPartsProcurementWO &&
           !wo.isEdrWorkOrder &&
           !wo.linkedWorkOrderId;
  };

  const canStoreAction = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    if (amMroBlocked(wo, 'store')) return false;
    // Only STORE MANAGER (STORE INSPECTOR), ACCOUNTABLE_MANAGER, and SUPER_ADMIN can approve
    // STORE_EXECUTIVE cannot perform ACCEPT/REJECT actions per SCOPE-Role-Structure.md
    const canPerformApproval = canApprove(user?.role) && 
                               (user?.role === ROLES.STORE_INSPECTOR || isOverride);
    return canPerformApproval && 
           wo.needsStoreAction && 
           (isOverride || wo.planningApproved) && 
           needsStoreIntentAction(wo);
  };

  const canMROAction = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    if (amMroBlocked(wo, 'mro_accept')) return false;
    // Only MRO MANAGER, ACCOUNTABLE_MANAGER, and SUPER_ADMIN can approve
    // MRO_ENGINEER (EXECUTIVE) cannot perform ACCEPT/REJECT actions per SCOPE-Role-Structure.md
    const canPerformApproval = canApprove(user?.role) && 
                               (user?.role === ROLES.MRO_MANAGER || isOverride);
    return canPerformApproval && 
           wo.needsMROAction && 
           (isOverride || wo.planningApproved) && 
           !wo.mroAccepted && !wo.mroRejectedAt;
  };

  /** Planning or CAMO upload CRS/CAME docs for outsourced work, then hand off to CAMO QM completion */
  const canExternalAgencyAction = (wo) => {
    if (isCancelledWorkOrder(wo) || !wo) return false;
    if (!wo.needsExternalAgencyAction || wo.externalAgencyCompleted) return false;
    if (!wo.planningApproved && !isOverride) return false;
    if (wo.needsStoreAction && !wo.storeAccepted && !isOverride) return false;
    if (wo.needsMROAction && !wo.mroAccepted && !isOverride) return false;
    return [
      ROLES.PLANNING_MANAGER,
      ROLES.CA_MANAGER,
      ROLES.CA_TECHNICAL_OFFICER,
      ROLES.CAMO_QM_MANAGER,
      ROLES.CAMO_QM_OFFICER,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.AM_MRO,
      ROLES.SUPER_ADMIN
    ].includes(user?.role);
  };

  // AuthContext / login payload uses `id`; some callers may still pass `_id`
  const getAuthUserId = () => {
    const raw = user?.id || user?._id;
    return raw ? String(raw) : '';
  };

  const getMroCurrentEngineerId = (wo) => {
    const current = wo?.mroCurrentEngineerId || wo?.mroAssignedEngineerId;
    if (!current) return null;
    return String(current._id || current);
  };

  const getMroTeamIds = (wo) => {
    const fromArray = Array.isArray(wo?.mroAssignedEngineerIds)
      ? wo.mroAssignedEngineerIds.map((e) => String(e?._id || e))
      : [];
    const legacy = wo?.mroAssignedEngineerId
      ? [String(wo.mroAssignedEngineerId._id || wo.mroAssignedEngineerId)]
      : [];
    return [...new Set([...fromArray, ...legacy].filter(Boolean))];
  };

  const canMROAssignEngineers = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    const hasRole = user?.role === ROLES.MRO_MANAGER || isOverride;
    return hasRole &&
           wo.mroAccepted &&
           wo.status === 'IN_PROGRESS' &&
           !wo.mroEngineerCompleted;
  };

  const canMROHandover = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    if (!wo?.mroAccepted || wo.mroEngineerCompleted || wo.status !== 'IN_PROGRESS') return false;
    if (user?.role === ROLES.MRO_MANAGER || isOverride) return true;
    if (user?.role !== ROLES.MRO_ENGINEER) return false;
    const currentId = getMroCurrentEngineerId(wo);
    const authUserId = getAuthUserId();
    // If no current owner yet, allow handover only after someone is assigned
    if (!currentId || !authUserId) return false;
    return currentId === authUserId;
  };

  const canMROEngineerComplete = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    if (amMroBlocked(wo, 'mro_engineer_complete')) return false;
    const privilegedEmergency = isStandaloneEmergencyActor(wo);
    const hasRole = [
      ROLES.MRO_ENGINEER,
      ROLES.CAMO_QM_MANAGER,
      ROLES.CAMO_QM_OFFICER
    ].includes(user?.role) || isOverride || privilegedEmergency;
    if (!hasRole) return false;
    if (!(isOverride || wo.mroAccepted || privilegedEmergency)) return false;
    if (wo.status !== 'IN_PROGRESS' || wo.mroEngineerCompleted) return false;

    // Sequential shifts: only current engineer may complete (managers / AM / SA bypass)
    if (user?.role === ROLES.MRO_ENGINEER && !isOverride && !privilegedEmergency) {
      const currentId = getMroCurrentEngineerId(wo);
      const authUserId = getAuthUserId();
      // Unassigned WOs stay visible to all engineers; once assigned, only the current owner
      if (currentId && currentId !== authUserId) return false;
    }
    return true;
  };

  const canMROQMCompletionAck = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    if (amMroBlocked(wo, 'mro_qm_completion')) return false;
    const privilegedEmergency = isStandaloneEmergencyActor(wo);
    const hasRole = MRO_QM_ACK_ROLES.includes(user?.role) || isOverride || privilegedEmergency;
    return hasRole &&
           (isOverride || wo.mroEngineerCompleted || privilegedEmergency) &&
           wo.status === 'IN_PROGRESS' &&
           !wo.mroQmCompletionAcknowledged;
  };

  const canCAMOQMCompletionAck = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    const privilegedEmergency = isStandaloneEmergencyActor(wo);
    // CAMO QM completion — org AM / Super Admin only (not AM_MRO)
    const hasRole = CAMO_QM_ACK_ROLES.includes(user?.role) || isAmOverride || privilegedEmergency;
    return hasRole &&
           (isAmOverride || (wo.mroEngineerCompleted && wo.mroQmCompletionAcknowledged) || privilegedEmergency) &&
           wo.status === 'IN_PROGRESS' &&
           !wo.camoQmCompletionAcknowledged;
  };

  const canViewCamoQmReviewTab = (wo) => {
    if (!wo || isCancelledWorkOrder(wo)) return false;
    const isQmRole = CAMO_QM_ACK_ROLES.includes(user?.role) || isAmOverride;
    if (!isQmRole) return false;
    const pendingCreation = wo.status === 'SCHEDULED' && !wo.camoQmAcknowledged;
    const pendingCompletion = wo.status === 'IN_PROGRESS'
      && wo.mroEngineerCompleted
      && wo.mroQmCompletionAcknowledged
      && !wo.camoQmCompletionAcknowledged;
    return pendingCreation || pendingCompletion || canAcknowledge(wo) || canMROQMCompletionAck(wo) || canCAMOQMCompletionAck(wo);
  };

  const canComplete = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    if (
      wo.standaloneEmergencyRepair &&
      [ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role)
    ) {
      return wo.status === 'IN_PROGRESS';
    }
    return wo.mroEngineerCompleted &&
           wo.mroQmCompletionAcknowledged &&
           wo.camoQmCompletionAcknowledged &&
           wo.status === 'IN_PROGRESS';
  };

  const canCreatePartsProcurement = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    const hasRole = user?.role === ROLES.STORE_INSPECTOR || isOverride;
    return hasRole && 
           wo.needsStoreAction && 
           (isOverride || wo.planningApproved) && 
           wo.partsProcurementRequirements?.partNumbers?.length > 0 &&
           !wo.isPartsProcurementWO &&
           !wo.isEdrWorkOrder &&
           !wo.linkedWorkOrderId;
  };

  const canIssueReleaseCertificate = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    const isCertifyingStaff = [ROLES.CA_MANAGER, ROLES.CAMO_QM_MANAGER, ROLES.MRO_QM_MANAGER].includes(user?.role);
    const releaseOverride = [ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
    const emergencyReleasePrivileged =
      wo.standaloneEmergencyRepair &&
      [ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
    
    if (!isCertifyingStaff && !releaseOverride) return false;
    if (wo.status !== 'COMPLETED') return false;
    if (!emergencyReleasePrivileged && (!wo.mroEngineerCompleted || !wo.mroQmCompletionAcknowledged || !wo.camoQmCompletionAcknowledged)) return false;
    
    // Check for unresolved discrepancies
    if (wo.discrepancies && wo.discrepancies.length > 0) {
      const unresolved = wo.discrepancies.filter(d => !d.resolved);
      if (unresolved.length > 0 && !releaseOverride && !emergencyReleasePrivileged) return false;
    }
    
    return !wo.maintenanceRelease?.releasedAt; // Not already issued
  };

  const canResumeFromHold = (wo) => {
    if (isCancelledWorkOrder(wo)) return false;
    const allowedRoles = [ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.STORE_INSPECTOR, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN];
    return allowedRoles.includes(user?.role) && 
           wo.status === 'HOLD' && 
           wo.linkedWorkOrderId; // Has parts procurement WO
  };

  const canCreateIntimation = (item) => {
    const hasRole =
      [
        ROLES.PLANNING_MANAGER,
        ROLES.PLANNING_OFFICER,
        ROLES.CA_MANAGER,
        ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER ].includes(user?.role) || isAmOverride;
    if (!hasRole) return false;
    
    // For work orders, check if intimation already exists
    if (item.source === 'WORK_ORDER' && item.sourceId) {
      // We'll need to check the actual work order, but for now allow if it's a work order
      return true;
    }
    
    // For other sources (component inspections/services), allow creating intimation
    return true;
  };

  const canLinkIntimationSource = () => {
    return [
      ROLES.PLANNING_MANAGER,
      ROLES.PLANNING_OFFICER,
      ROLES.CA_MANAGER,
      ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER ].includes(user?.role) || isAmOverride;
  };

  // Can user respond to standalone intimations (Intimations tab). Role-only; no WO fields.
  const canRespondToIntimation = () => {
    return [ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER].includes(user?.role) || isAmOverride;
  };

  const canRequestDueExtension = (item) => {
    if (!item?.source || item.source === 'WORK_ORDER') return false;
    return [ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
  };

  const canApproveDueExtension = isAmOverride || user?.role === ROLES.AM_MRO;
  const canRevokeDueExtension = isAmOverride || user?.role === ROLES.AM_MRO;

  // CAMO / Planning edit suite — AM_MRO edits via planning/MRO domain override, not CAMO TO
  const canManageMaintenance = [ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role)
    || user?.role === ROLES.AM_MRO;

  const requiresAmOverrideForWorkOrderChange =
    user?.role === ROLES.ACCOUNTABLE_MANAGER || user?.role === ROLES.AM_MRO;

  const canEditWorkOrder = (wo) => {
    if (!wo || isCancelledWorkOrder(wo) || wo.status === 'COMPLETED') return false;
    if ([ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role)) return true;
    if (requiresAmOverrideForWorkOrderChange) return true;
    return false;
  };

  const canCancelWorkOrder = (wo) => {
    if (!wo) return false;
    if (isCancelledWorkOrder(wo) || wo.status === 'COMPLETED') return false;
    return [
      ROLES.CA_MANAGER,
      ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.AM_MRO,
      ROLES.SUPER_ADMIN
    ].includes(user?.role);
  };

  const canEditWorkOrderScheduledDate = (wo) => {
    if (!wo || !user?.role || isCancelledWorkOrder(wo)) return false;
    return canUserChangeWorkOrderScheduledDate(wo, user.role);
  };

  const canViewMaintenance = roleMatches(user?.role, [
    ROLES.CA_MANAGER,
    ROLES.CA_TECHNICAL_OFFICER,
    ROLES.CAMO_QM_MANAGER,
    ROLES.CAMO_QM_OFFICER,
    ROLES.PLANNING_MANAGER,
    ROLES.STORE_INSPECTOR,
    ROLES.MRO_MANAGER,
    ROLES.MRO_ENGINEER,
    ROLES.MRO_QM_MANAGER,
    ROLES.MRO_QM_OFFICER,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ]);

  /** EDR inbox on Maintenance & Inspections: all CAMO roles + Planning Manager */
  const canViewEdrInbox = roleMatches(user?.role, EDR_INBOX_ROLES);

  return {
    canAcknowledge,
    canPlan,
    canProvideProcurementRequirements,
    canStoreAction,
    canMROAction,
    canExternalAgencyAction,
    canMROAssignEngineers,
    canMROHandover,
    canMROEngineerComplete,
    getMroCurrentEngineerId,
    getMroTeamIds,
    canMROQMCompletionAck,
    canCAMOQMCompletionAck,
    canViewCamoQmReviewTab,
    canComplete,
    canCreatePartsProcurement,
    canIssueReleaseCertificate,
    canResumeFromHold,
    canCreateIntimation,
    canLinkIntimationSource,
    canRequestDueExtension,
    canApproveDueExtension,
    canRevokeDueExtension,
    canRespondToIntimation,
    canManageMaintenance,
    canEditWorkOrder,
    canCancelWorkOrder,
    requiresAmOverrideForWorkOrderChange,
    canEditWorkOrderScheduledDate,
    canViewMaintenance,
    canViewEdrInbox,
    canStandAloneEmergencyWorkOrder,
    canViewStandaloneEmergencyTab,
    getAmMroConsecutiveStepWarning: (wo, step) => amMroWarning(wo, step)
  };
};
